// The honest end of the funnel for a patient who already has an Expy account.
//
// Two ways in, one screen:
//   'existing'  POST /public/account found a User with the intake's email. The server
//               links the intake to that account (no second account, no second plan,
//               nothing overwritten) and sends NO email, so this screen must not
//               pretend a sign-in link is on its way. A "want a PT" intake now sits on
//               the staff PT-requests queue against their account; that is the promise.
//   'login'     The "Already have an account? Log in instead" link on the account form.
//               Nothing was submitted, so nothing is linked yet: point them at the app,
//               and offer the way back to the form for anyone who does want a match.
//
// Same centred single-column layout as CheckEmail; store badges shared with GetTheApp.
import type { Answers } from '../lib/session';
import { providerNoun } from '../lib/noun';
import { StoreBadges } from '../components/StoreBadges';

export type ExistingReason = 'existing' | 'login';

export function ExistingAccount({
  answers,
  reason,
  onBack,
}: {
  answers: Answers;
  reason: ExistingReason;
  onBack: () => void;
}) {
  const noun = providerNoun(answers.country as string | undefined);
  // Only a WANT_PT intake becomes a matching request; for everyone else the honest
  // claim is simply that their answers stay with their account.
  const wantsMatch = answers.intent === 'WANT_PT';
  const heading = reason === 'existing' ? 'You already have an Expy account.' : 'Sign in on the app.';
  const body =
    reason === 'existing'
      ? wantsMatch
        ? `We added your answers to your request for a ${noun}. Sign in to the Expy app and we will take it from there.`
        : 'We kept your answers with your account. Sign in to the Expy app to carry on with your plan.'
      : 'Expy lives on your phone. Open the Expy app and sign in with the email you already use.';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: 18, background: 'var(--teal-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px' }}>
          <svg width={30} height={30} viewBox="0 0 24 24" fill="none" aria-hidden>
            <rect x={7} y={2.5} width={10} height={19} rx={2.5} stroke="var(--teal-deep)" strokeWidth={1.8} />
            <path d="M10.5 18h3" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" />
          </svg>
        </div>
        <div className="pretty" style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.16 }}>{heading}</div>
        <div className="pretty" style={{ fontSize: 16, fontWeight: 500, color: 'var(--slate)', marginTop: 12, lineHeight: 1.55 }}>{body}</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--slate)', marginTop: 16 }}>
          Forgot your password? Reset it from the app’s sign-in screen.
        </div>
        <StoreBadges style={{ justifyContent: 'center', marginTop: 26 }} />
        {reason === 'login' && (
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 22, lineHeight: 1.5 }}>
            {wantsMatch && (
              <>Want a {noun} through Expy? Finish the form and we will add your answers to your existing account. No second account, no second plan.{' '}</>
            )}
            <button onClick={onBack} style={{ color: 'var(--teal-deep)', fontWeight: 800 }}>Back to the form</button>
          </div>
        )}
      </div>
    </div>
  );
}
